import React, { useEffect, useState } from 'react';
import { collection, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../services/firebase';
import moment from 'moment';
import { CalendarDays, Clock, MapPin, TrendingUp } from 'lucide-react';
import PredictionForm from './PredictionForm';
import UserPredictions from './UserPredictions';

const Matches = () => {
  const [liveMatches, setLiveMatches] = useState([]);
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPredictionForm, setShowPredictionForm] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [viewMode, setViewMode] = useState('matches'); // 'matches' or 'predictions'
  
  useEffect(() => {
    const fetchMatches = async () => {
      try {
        const now = moment.utc();
        const matchesRef = collection(db, 'matches');
        
        // Set up real-time listener for matches
        const unsubscribe = onSnapshot(matchesRef, (snapshot) => {
          const matches = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          
            const live = matches.filter(match => 
    moment.utc(match.dateTimeGMT).isSameOrBefore(now) && !match.hasEnded
  );

  const upcoming = matches.filter(match => 
    moment.utc(match.dateTimeGMT).isAfter(now)
  );

          upcoming.sort((a, b) => new Date(a.dateTimeGMT) - new Date(b.dateTimeGMT));

          setLiveMatches(live);
          setUpcomingMatches(upcoming);
          setLoading(false);
        }, error => {
          console.error('Error in onSnapshot:', error);
          setLoading(false);
        });
        
        // Return the unsubscribe function directly
        return unsubscribe;
      } catch (error) {
        console.error('Error fetching matches:', error);
        setLoading(false);
        // Return a no-op function if there's an error
        return () => {};
      }
    };

    // Call fetchMatches and store the unsubscribe function
    const unsubscribeFunc = fetchMatches();
    
    // Clean up function for useEffect
    return () => {
      // Check if unsubscribeFunc exists and is a function before calling it
      if (unsubscribeFunc && typeof unsubscribeFunc.then === 'function') {
        // Handle the case where unsubscribeFunc is a Promise
        unsubscribeFunc.then(unsubFn => {
          if (unsubFn && typeof unsubFn === 'function') {
            unsubFn();
          }
        }).catch(err => console.error('Error in unsubscribe:', err));
      } else if (typeof unsubscribeFunc === 'function') {
        // Handle the case where unsubscribeFunc is directly a function
        unsubscribeFunc();
      }
    };
  }, []);

  const handlePredictionClick = (match) => {
    setSelectedMatch(match);
    setShowPredictionForm(true);
  };

  const handleClosePredictionForm = () => {
    setShowPredictionForm(false);
    setSelectedMatch(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 via-blue-950 to-gray-950">
      <div className="max-w-4xl mx-auto px-6 py-10 relative">
        {/* Use CSS grid pattern instead of PNG */}
        <div className="absolute inset-0 bg-[radial-gradient(#ffffff11_1px,transparent_1px)] bg-[length:20px_20px] opacity-10 mix-blend-overlay pointer-events-none"></div>
        
        {/* Glowing cricket ball effect in background */}
        <div className="absolute top-20 right-10 w-56 h-56 rounded-full bg-yellow-500 opacity-5 blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-40 left-10 w-72 h-72 rounded-full bg-blue-500 opacity-5 blur-3xl pointer-events-none"></div>
        <div className="absolute top-1/2 left-1/4 w-48 h-48 rounded-full bg-green-500 opacity-5 blur-3xl pointer-events-none"></div>
        
        <h1 className="text-4xl font-bold text-center mb-10 text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-300 drop-shadow-sm relative">
          Cricket Predictions
          <div className="w-32 h-1 bg-gradient-to-r from-yellow-500 to-transparent rounded-full mx-auto mt-2"></div>
        </h1>
      
        {/* View Toggle */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex rounded-md shadow-lg backdrop-blur-sm" role="group">
            <button 
              type="button" 
              className={`px-6 py-3 text-sm font-medium border border-gray-700 rounded-l-lg ${viewMode === 'matches' ? 'bg-blue-600 text-white shadow-md shadow-blue-900/50' : 'bg-gray-800/80 text-gray-300 hover:bg-gray-700/80'}`}
              onClick={() => setViewMode('matches')}
            >
              Matches
            </button>
            <button 
              type="button" 
              className={`px-6 py-3 text-sm font-medium border border-gray-700 rounded-r-lg ${viewMode === 'predictions' ? 'bg-blue-600 text-white shadow-md shadow-blue-900/50' : 'bg-gray-800/80 text-gray-300 hover:bg-gray-700/80'}`}
              onClick={() => setViewMode('predictions')}
            >
              Your Predictions
            </button>
          </div>
        </div>
      
        {viewMode === 'predictions' ? (
          <UserPredictions />
        ) : (
          <>
            {/* Live Matches Section */}
            <div className="mb-12 relative">
              <h2 className="text-2xl font-bold mb-6 text-red-500 flex items-center gap-2">
                <span className="h-3 w-3 rounded-full bg-red-500 animate-pulse inline-block" /> Live Matches
              </h2>
              {liveMatches.length === 0 ? (
                <div className="bg-gray-900/70 backdrop-blur-sm rounded-lg p-8 text-center border border-gray-800/50 shadow-lg">
                  <div className="text-gray-300 mb-2">No live matches at the moment</div>
                  <p className="text-sm text-gray-500">Check the upcoming section for scheduled matches</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {liveMatches.map(match => (
                    <MatchCard 
                      key={match.id} 
                      match={match} 
                      isLive={true}
                      onPredictionClick={handlePredictionClick}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Upcoming Matches Section */}
            <div className="relative">
              <h2 className="text-2xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-300">Upcoming Matches</h2>
              {upcomingMatches.length === 0 ? (
                <div className="bg-gray-900/70 backdrop-blur-sm rounded-lg p-8 text-center border border-gray-800/50 shadow-lg">
                  <div className="text-gray-300 mb-2">No upcoming matches</div>
                  <p className="text-sm text-gray-500">Check back later for scheduled matches</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {upcomingMatches.map(match => (
                    <MatchCard 
                      key={match.id} 
                      match={match} 
                      isLive={false}
                      onPredictionClick={handlePredictionClick}
                    />
                  ))}
                </div>
              )}
            </div>
          
            {/* Prediction Form Modal */}
            {showPredictionForm && selectedMatch && (
              <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <div className="max-w-md w-full">
                  <PredictionForm match={selectedMatch} onClose={handleClosePredictionForm} />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const MatchCard = ({ match, isLive, onPredictionClick }) => {
  const matchDate = moment(match.dateTimeGMT);
  // Extract match type (assuming it can be found in the name or derived from match data)
  const matchType = match.matchType || match.scorecard?.series || "T20";
  
  // Handle different team data structures
  const team1 = match.team1 || (match.teams && match.teams[0]) || '';
  const team2 = match.team2 || (match.teams && match.teams[1]) || '';

  // Default cricket ball icon for team logos if not available
  const defaultTeamLogo = 'https://img.icons8.com/color/96/cricket-ball.png';

  return (
    <div className="bg-gray-900/80 backdrop-blur-sm rounded-lg overflow-hidden border border-gray-800/50 hover:border-blue-700/50 transition-all duration-300 shadow-lg transform hover:-translate-y-1 group relative">
      {/* Add subtle animated shine effect on hover */}
      <div className="absolute -inset-px bg-gradient-to-r from-transparent via-blue-400/10 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-shine pointer-events-none"></div>
      
      <div className="px-4 py-3 flex items-center gap-3 bg-gradient-to-r from-gray-900 to-gray-800">
        {isLive ? (
          <div className="bg-red-600 text-white text-xs font-medium px-3 py-1 rounded-full flex items-center shadow-md shadow-red-900/30">
            <span className="animate-ping absolute h-2 w-2 rounded-full bg-white opacity-75 mr-1"></span>
            <span className="relative rounded-full h-2 w-2 bg-white mr-1"></span>
            LIVE
          </div>
        ) : (
          <div className="bg-gradient-to-r from-orange-600 to-amber-500 text-white text-xs font-medium px-3 py-1 rounded-full shadow-md shadow-orange-900/30">
            Upcoming
          </div>
        )}
        <span className="text-gray-400 text-sm">{matchType}</span>
      </div>

      <div className="p-5 pb-0">
        <h2 className="text-lg font-medium text-white mb-6 group-hover:text-blue-300 transition-colors">{match.name}</h2>

        <div className="flex items-center justify-between mb-6">
          <div className="bg-gradient-to-b from-gray-800 to-gray-900 rounded-md p-4 w-[42%] text-center shadow-inner shadow-black/30 transition-all group-hover:bg-gradient-to-b group-hover:from-gray-800 group-hover:to-gray-900/80">
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 p-3 bg-white rounded-full mb-3 flex items-center justify-center shadow-md">
                {match.team1img && match.team1img.trim() !== '' ? (
                <img src={match.team1img} alt={team1} className="max-w-full max-h-full" />
                ) : (
                  <img src={defaultTeamLogo} alt={team1} className="max-w-full max-h-full" />
                )}
              </div>
              <p className="font-medium text-white text-center">{team1}</p>
            </div>
            {isLive && match.scorecard?.team1Score && (
              <div className="mt-3 font-bold text-lg text-blue-300 bg-blue-900/30 px-4 py-1 rounded-full">
                {match.scorecard.team1Score}
              </div>
            )}
          </div>
          <div className="text-gray-500 font-medium relative">
            <span className="absolute -top-1 -left-3 text-yellow-500/30 text-xs">VS</span>
            VS
            <span className="absolute -bottom-1 -right-3 text-yellow-500/30 text-xs">VS</span>
          </div>
          <div className="bg-gradient-to-b from-gray-800 to-gray-900 rounded-md p-4 w-[42%] text-center shadow-inner shadow-black/30 transition-all group-hover:bg-gradient-to-b group-hover:from-gray-800 group-hover:to-gray-900/80">
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 p-3 bg-white rounded-full mb-3 flex items-center justify-center shadow-md">
                {match.team2img && match.team2img.trim() !== '' ? (
                <img src={match.team2img} alt={team2} className="max-w-full max-h-full" />
                ) : (
                  <img src={defaultTeamLogo} alt={team2} className="max-w-full max-h-full" />
                )}
              </div>
              <p className="font-medium text-white text-center">{team2}</p>
            </div>
            {isLive && match.scorecard?.team2Score && (
              <div className="mt-3 font-bold text-lg text-blue-300 bg-blue-900/30 px-4 py-1 rounded-full">
                {match.scorecard.team2Score}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="px-5 pb-4 flex flex-wrap items-center gap-3 mb-4 text-gray-400/80">
        <div className="flex items-center text-sm">
          <Clock size={16} className="mr-1 text-blue-400/70" />
          <span>{matchDate.format('h:mm A')}</span>
        </div>
        <div className="flex items-center text-sm">
          <CalendarDays size={16} className="mr-1 text-blue-400/70" />
          <span>{matchDate.format('MMM DD, YYYY')}</span>
        </div>
        {match.venue && (
          <div className="flex items-center text-sm">
            <MapPin size={16} className="mr-1 text-blue-400/70" />
            <span>{match.venue}</span>
          </div>
        )}
      </div>

      {isLive && match.scorecard?.status && (
        <div className="px-5 pb-4 text-sm text-blue-300 bg-blue-900/20 mx-5 rounded p-2 border-l-2 border-blue-500">
          {match.scorecard.status}
        </div>
      )}

      <div className="p-5 pt-3">
        <button 
          className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white w-full py-3 rounded-md flex items-center justify-center transition-all duration-300 text-sm font-medium shadow-md shadow-blue-900/50 hover:shadow-lg relative overflow-hidden group-hover:scale-[1.02]"
          onClick={() => onPredictionClick(match)}
        >
          <span className="relative z-10 flex items-center">
            <TrendingUp size={16} className="mr-2" />
            Make Prediction
          </span>
          {/* Add subtle shine animation to button */}
          <span className="absolute top-0 -inset-full h-full w-1/2 z-5 block transform -skew-x-12 bg-gradient-to-r from-transparent to-white opacity-20 group-hover:animate-shine"></span>
        </button>
      </div>
    </div>
  );
};

export default Matches;